import { API_BASE_URL } from '../../config/apiConfig';
import { getAuthGeneration, getAuthToken } from '../../utils/auth';
import { dispatchUnauthorizedAuthEvent } from '../../utils/authSession';
import { fetchWithCookieCsrf } from '../csrf';
import type { RoleplaySession, RoleplayTurnProgress, RoleplayTurnStreamOptions } from './roleplayClient';

const IDLE_TIMEOUT_MS = 60000;
const MAX_FRAME_LENGTH = 2 * 1024 * 1024;

/** Only public errors explicitly returned by the roleplay API use this type. */
export class RoleplayStreamError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'RoleplayStreamError';
  }
}

/** One accepted turn, never automatically reposted if the stream is interrupted. */
export const submitRoleplayStream = async (
  path: string,
  payload: unknown,
  options: RoleplayTurnStreamOptions,
): Promise<{ data: RoleplaySession }> => {
  const controller = new AbortController();
  const abort = () => controller.abort();
  const waitForTransport = <T,>(pending: Promise<T>): Promise<T> => new Promise((resolve, reject) => {
    const canceled = () => reject(new DOMException('Request canceled.', 'AbortError'));
    controller.signal.addEventListener('abort', canceled, { once: true });
    if (controller.signal.aborted) canceled();
    pending.then(resolve, reject).finally(() => controller.signal.removeEventListener('abort', canceled));
  });
  options.signal?.addEventListener('abort', abort, { once: true });
  if (options.signal?.aborted) abort();
  let idleTimer: ReturnType<typeof setTimeout>;
  let timedOut = false;
  const resetTimer = () => {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => { timedOut = true; controller.abort(); }, IDLE_TIMEOUT_MS);
  };
  const token = getAuthToken();
  const authGeneration = getAuthGeneration();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json', Accept: 'text/event-stream',
  };
  if (token) headers.Authorization = `Token ${token}`;
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  resetTimer();
  try {
    if (controller.signal.aborted) throw new DOMException('Request canceled.', 'AbortError');
    const response = await waitForTransport(fetchWithCookieCsrf(`${API_BASE_URL}${path}`, {
      method: 'POST', headers, body: JSON.stringify(payload),
      credentials: 'include', signal: controller.signal,
    }));
    if (!response.ok) {
      if (response.status === 401 && authGeneration === getAuthGeneration()) {
        dispatchUnauthorizedAuthEvent({ reason: 'session_expired', status: 401, source: 'roleplayStream' });
      }
      // Read only the API's public detail contract, never proxy HTML or other
      // response fields that might contain internal diagnostics.
      let detail: unknown;
      if (response.headers.get('Content-Type')?.includes('application/json')) {
        try { detail = (await waitForTransport(response.json()))?.detail; } catch { /* Use the public fallback. */ }
      }
      if (controller.signal.aborted) throw new DOMException('Request canceled.', 'AbortError');
      throw new RoleplayStreamError(
        typeof detail === 'string' && detail.trim() ? detail : `Roleplay request failed (HTTP ${response.status}).`,
        response.status,
      );
    }
    reader = response.body?.getReader();
    if (!reader) throw new Error('The roleplay response stream is unavailable.');
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await waitForTransport(reader.read());
      if (controller.signal.aborted) throw new DOMException('Request canceled.', 'AbortError');
      if (done) throw new Error('The roleplay response ended before completion.');
      resetTimer();
      buffer += decoder.decode(value, { stream: true });
      if (buffer.length > MAX_FRAME_LENGTH) throw new Error('The roleplay response frame is too large.');
      let boundary: RegExpExecArray | null;
      // Match delimiters before normalizing so CRLF split across chunks is safe.
      while ((boundary = /\r?\n\r?\n/.exec(buffer))) {
        const frame = buffer.slice(0, boundary.index);
        buffer = buffer.slice(boundary.index + boundary[0].length);
        const data = frame.split(/\r?\n/).filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).replace(/^ /, '')).join('\n');
        if (!data) continue;
        let event: RoleplayTurnProgress | { type: 'error'; detail?: string } | { type: 'ping' };
        try { event = JSON.parse(data); } catch { throw new Error('The roleplay response contains an invalid frame.'); }
        if (!event || typeof event !== 'object') throw new Error('The roleplay response contains an invalid event.');
        if (event.type === 'ping') continue;
        if (event.type === 'error') throw new RoleplayStreamError(
          typeof event.detail === 'string' && event.detail.trim()
            ? event.detail : 'The roleplay response could not be completed.',
        );
        if (!['provisional', 'preview', 'session'].includes(event.type)
          || !('session' in event) || !event.session || typeof event.session.id !== 'number'
          || !Array.isArray(event.session.turns)) {
          throw new Error('The roleplay response contains an invalid session.');
        }
        if (controller.signal.aborted) throw new DOMException('Request canceled.', 'AbortError');
        options.onProgress(event);
        if (event.type === 'session') return { data: event.session };
      }
    }
  } catch (error) {
    if (timedOut) throw new Error('The roleplay response timed out. Checking the saved attempt.');
    throw error;
  } finally {
    clearTimeout(idleTimer!);
    options.signal?.removeEventListener('abort', abort);
    // Cancel remaining bytes after the authoritative frame, including on error.
    void reader?.cancel().catch(() => undefined);
    controller.abort();
  }
};
